import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';

export function bundleAssetUrl(url) {
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  const origin = API_BASE_URL.replace(/\/api\/?$/, '');
  return `${origin}${url.startsWith('/') ? '' : '/'}${url}`;
}

export function bundleImage(bundle) {
  if (bundle?.coverImage) return bundleAssetUrl(bundle.coverImage);
  const first = bundle?.designs?.find((d) => d && !d.missing);
  return first ? `${API_BASE_URL}/designs/${first.id}/thumbnail` : '';
}

function BundlesPage() {
  const [bundles, setBundles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await axios.get(`${API_BASE_URL}/bundles`);
        if (!cancelled && response.data.success) {
          setBundles(response.data.data || []);
        }
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.error || err.message || 'Failed to load bundles');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Loading bundles...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center max-w-md">
          <h2 className="text-xl font-semibold text-red-800 mb-2">Bundles unavailable</h2>
          <p className="text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 lg:py-12 px-4 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <header className="mb-6 md:mb-10">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Bundles</h1>
          <p className="mt-1 text-sm md:text-base text-gray-600">
            Curated sets of designs, sold together.
          </p>
        </header>

        {bundles.length === 0 ? (
          <p className="text-gray-500 text-lg text-center py-16">No bundles available yet.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {bundles.map((bundle) => {
              const image = bundleImage(bundle);
              const count = (bundle.designIds || []).length;
              const presentationLabel =
                bundle.presentation?.kind === 'custom'
                  ? bundle.presentation.label
                  : bundle.presentation?.formatId;
              return (
                <Link
                  key={bundle.id}
                  to={`/bundle/${bundle.id}`}
                  className="bg-white rounded-xl shadow-md hover:shadow-xl transition-shadow overflow-hidden border border-gray-100"
                >
                  <div className="aspect-[4/3] bg-gray-100 overflow-hidden">
                    {image ? (
                      <img src={image} alt={bundle.title} className="w-full h-full object-cover" loading="lazy" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-400 text-sm">
                        No image
                      </div>
                    )}
                  </div>
                  <div className="p-4 md:p-5">
                    <h2 className="text-lg font-semibold text-gray-900">{bundle.title}</h2>
                    {bundle.theme && (
                      <p className="text-xs uppercase tracking-wide text-gray-500 mt-1">{bundle.theme}</p>
                    )}
                    <p className="text-sm text-gray-600 mt-2">
                      {count} design{count !== 1 ? 's' : ''}
                      {presentationLabel ? ` · ${presentationLabel}` : ''}
                    </p>
                    <div className="mt-3 flex items-center justify-between">
                      <span className="text-xl font-bold text-gray-900">${Number(bundle.price).toFixed(2)}</span>
                      <span className="text-sm font-medium text-blue-600">View bundle →</span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default BundlesPage;
